import axiosInstance from "@/api/axiosInstance";
import {
  KiteVersion,
  KiteVersionPaginatedResponse,
  KiteVersionRequestDTO,
} from "@/common/model/KiteVersion";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";

// Re-export type for compatibility with components
export type { KiteVersion };

export const KiteVersionService = {
  /**
   * Get paginated versions with sorting and filtering options
   * @param limit - Number of results per page
   * @param sortField - Field to sort by
   * @param sortOrder - Sort direction
   * @param page - Page number
   * @param searchValue - Optional search term
   * @returns Paginated response with versions
   */
  async getVersions(
    limit: number = 10,
    sortField: string = "version",
    sortOrder: string = "asc",
    page: number = 1,
    searchValue?: string
  ): Promise<KiteVersionPaginatedResponse> {
    return requestHandler<KiteVersionPaginatedResponse>(async () => {
      let url = `${ENDPOINTS.GEAR.VERSIONS_PAGINATED}?limit=${limit}&sortField=${sortField}&sortOrder=${sortOrder}&page=${page}`;

      if (searchValue) {
        url += `&searchval=${encodeURIComponent(searchValue)}`;
      }

      const response = await axiosInstance.get(url);
      const data = response.data;

      // Assign totalItems as alias of totalVersions for compatibility with the generic component
      if (data && typeof data.totalVersions === "number") {
        data.totalItems = data.totalVersions;
      }

      return data;
    }, "KiteVersionService -> getVersions");
  },

  /**
   * Search for versions by name
   * @param searchValue - The search term (minimum 3 characters)
   * @param limit - Maximum number of results to return
   * @returns Array of matching versions
   */
  async searchVersions(
    searchValue: string,
    limit: number = 10
  ): Promise<KiteVersion[]> {
    // Only perform the search if there are at least 3 characters
    if (searchValue.length < 3) return [];

    return requestHandler<KiteVersion[]>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.VERSIONS_PAGINATED}?searchval=${encodeURIComponent(
          searchValue
        )}&limit=${limit}&sortField=version&sortOrder=asc`
      );
      return response.data?.data || [];
    }, "KiteVersionService -> searchVersions");
  },

  /**
   * Fetch a version by its ID
   * @param versionId - The ID of the version to fetch
   * @returns The version or null if not found
   */
  async getVersionById(versionId: number): Promise<KiteVersion | null> {
    return requestHandler<KiteVersion | null>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.VERSIONS_LIST}/${versionId}`
      );
      return response.data || null;
    }, "KiteVersionService -> getVersionById");
  },

  /**
   * Create a new version
   * @param versionData - The version data to create
   * @returns The created version or null if creation failed
   */
  async createVersion(
    versionData: KiteVersionRequestDTO
  ): Promise<KiteVersion | null> {
    return requestHandler<KiteVersion | null>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.GEAR.VERSIONS,
        versionData
      );
      return response.data || null;
    }, "KiteVersionService -> createVersion");
  },

  /**
   * Update an existing version
   * @param versionId - The ID of the version to update
   * @param versionData - The updated version data
   * @returns The updated version or null if update failed
   */
  async updateVersion(
    versionId: number,
    versionData: KiteVersionRequestDTO
  ): Promise<KiteVersion | null> {
    return requestHandler<KiteVersion | null>(async () => {
      const response = await axiosInstance.put(
        ENDPOINTS.GEAR.VERSIONS_UPDATE(versionId),
        versionData
      );
      return response.data || null;
    }, "KiteVersionService -> updateVersion");
  },

  /**
   * Delete a version
   * @param versionId - The ID of the version to delete
   * @returns True if deletion was successful, false otherwise
   */
  async deleteVersion(versionId: number): Promise<boolean> {
    return requestHandler<boolean>(async () => {
      const response = await axiosInstance.delete(
        ENDPOINTS.GEAR.VERSIONS_DELETE(versionId)
      );
      return response.status >= 200 && response.status < 300;
    }, "KiteVersionService -> deleteVersion");
  },
};
